import {
  Component,
  forwardRef,
  Input,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type Num = number | null;

@Component({
  selector: 'base-input-number',
  templateUrl: './base-input-number.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => BaseInputNumberComponent), multi: true }],
})
export class BaseInputNumberComponent implements ControlValueAccessor {
  @Input() min: number | undefined;
  @Input() max: number | undefined;
  @Input() step = 1;
  @Input() allowDecimal = false;
  @Input() precision: number | undefined;
  @Input() placeholder = '0';
  @Input() readonly = false;

  disabled = false;
  value: number = 0;
  display = '0';

  private onChange: (v: Num) => void = () => {};
  private onTouched: () => void = () => {};
  private holdDelayId: number | null = null;
  private repeatId: number | null = null;
  private isHolding = false;

  @ViewChild('inputEl', { static: true }) inputEl!: ElementRef<HTMLInputElement>;

  constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone) {}

  writeValue(v: Num): void {
    const n = v == null ? 0 : Math.max(0, Number(v));
    this.value = Number.isFinite(n) ? n : 0;
    this.display = this.toDisplay(this.value);
    if (this.inputEl) this.inputEl.nativeElement.value = this.display;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: Num) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onFocus(): void {
    if (this.disabled || this.readonly) return;
    setTimeout(() => {
      const el = this.inputEl.nativeElement;
      el.setSelectionRange(0, el.value.length);
    });
  }

  onInput(ev: Event): void {
    if (this.disabled || this.readonly) return;
    const el = ev.target as HTMLInputElement;
    const sanitized = this.sanitize(el.value);
    if (sanitized !== el.value) {
      el.value = sanitized;
      el.setSelectionRange(sanitized.length, sanitized.length);
    }
    this.display = sanitized;
  }

  onPaste(ev: ClipboardEvent): void {
    if (this.disabled || this.readonly) return;
    ev.preventDefault();
    const text = ev.clipboardData?.getData('text') ?? '';
    const sanitized = this.sanitize(text);
    this.insertAtSelection(sanitized);
  }

  onBlur(): void {
    if (this.disabled) return;
    const parsed = this.parse(this.display);
    const normalized = this.clampAndRound(parsed);
    this.value = normalized;
    this.display = this.toDisplay(normalized);
    this.inputEl.nativeElement.value = this.display;
    this.onTouched();
    this.onChange(this.value);
    this.cdr.markForCheck();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.readonly) return;
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.bump(event.key === 'ArrowUp' ? +this.step : -this.step);
    }
  }

  onArrowPointerDown(direction: 1 | -1, ev: PointerEvent): void {
    if (this.disabled || this.readonly) return;
    ev.preventDefault();
    this.bump(direction * this.step);

    this.ngZone.runOutsideAngular(() => {
      this.isHolding = true;
      const stop = () => this.stopHoldOutside();

      window.addEventListener('pointerup', stop, { passive: true, once: true });
      window.addEventListener('pointercancel', stop, { passive: true, once: true });
      window.addEventListener('blur', stop, { passive: true, once: true });

      const btn = ev.currentTarget as HTMLElement | null;
      if (btn) {
        btn.addEventListener('pointerleave', stop, { passive: true, once: true });
        btn.addEventListener('pointerout', stop, { passive: true, once: true });
      }

      this.holdDelayId = window.setTimeout(() => {
        this.repeatId = window.setInterval(() => {
          const base = this.parse(this.display) ?? this.value ?? 0;
          const next = this.clampAndRound(base + direction * this.step);
          if (next !== this.value) {
            this.ngZone.run(() => this.commitFromOutside(next));
          }
        }, 150);
      }, 300);
    });
  }

  private commitFromOutside(next: number): void {
    this.value = next;
    this.display = this.toDisplay(next);
    const el = this.inputEl.nativeElement;
    if (el.value !== this.display) el.value = this.display;
    this.onChange(this.value);
    this.cdr.markForCheck();
  }

  private stopHoldOutside(): void {
    if (!this.isHolding) return;
    this.isHolding = false;
    if (this.holdDelayId != null) {
      clearTimeout(this.holdDelayId);
      this.holdDelayId = null;
    }
    if (this.repeatId != null) {
      clearInterval(this.repeatId);
      this.repeatId = null;
    }
  }

  private bump(delta: number): void {
    const base = this.parse(this.display) ?? this.value ?? 0;
    const next = this.clampAndRound(base + delta);
    this.value = next;
    this.display = this.toDisplay(next);
    const el = this.inputEl.nativeElement;
    if (el.value !== this.display) el.value = this.display;
    this.onChange(this.value);
    el.focus();
    this.cdr.markForCheck();
  }

  private get effectiveMin(): number {
    return Math.max(0, this.min ?? 0);
  }

  private clampAndRound(v: Num): number {
    let n = v == null || Number.isNaN(v) ? 0 : v;
    n = Math.max(n, this.effectiveMin);
    if (typeof this.max === 'number') n = Math.min(n, this.max);
    if (!this.allowDecimal) n = Math.trunc(n);
    if (typeof this.precision === 'number' && this.allowDecimal) {
      const f = Math.pow(10, this.precision);
      n = Math.round(n * f) / f;
    }
    return n;
  }

  private toDisplay(v: number): string {
    return String(v);
  }

  private parse(s: string): Num {
    const t = s.trim();
    if (!t || t === '.') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }

  private sanitize(s: string): string {
    let out = '';
    let dotSeen = false;
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch >= '0' && ch <= '9') out += ch;
      else if (this.allowDecimal && ch === '.' && !dotSeen) {
        out += '.';
        dotSeen = true;
      }
    }
    return out;
  }

  private insertAtSelection(text: string): void {
    const el = this.inputEl.nativeElement;
    const { selectionStart, selectionEnd, value } = el;
    const s = selectionStart ?? value.length;
    const e = selectionEnd ?? value.length;
    const next = value.slice(0, s) + text + value.slice(e);
    const safe = this.sanitize(next);
    el.value = safe;
    const caret = Math.min(s + text.length, safe.length);
    el.setSelectionRange(caret, caret);
    this.display = safe;
  }
}
